import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { makeApiCall } from '../../config/api';

const LiveLogsReport = () => {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [selectedTrucks, setSelectedTrucks] = useState([]);
  const [geofenceName, setGeofenceName] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Fetch trucks list
  useEffect(() => {
    const fetchTrucks = async () => {
      try {
        const response = await makeApiCall('api/gps/locations/current');
        if (response.ok) {
          const result = await response.json();
          // Extract trucks from the result
          if (result.truck_locations) {
            setTrucks(result.truck_locations.map(t => ({
              id: t.truck_id,
              name: t.truck_name || t.fence_vehicle_name
            })));
          }
        }
      } catch (err) {
        console.error('Error fetching trucks:', err);
      }
    };
    fetchTrucks();

    // Auto-load today
    const to = new Date();
    const from = new Date();
    // Default to today start/end
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
    setFromDate(from);
    setToDate(to);
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate.toISOString());
      if (toDate) params.append('to_date', toDate.toISOString());
      
      if (selectedTrucks.length > 0) {
        const ids = selectedTrucks.map(t => t.id).join(',');
        params.append('truck_ids', ids);
      }
      
      if (geofenceName) {
        params.append('geofence_name', geofenceName);
      }

      const response = await makeApiCall(`api/reports/live-logs?${params}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          setError(t('reports.error.fetching'));
        }
      } else {
        setError(t('reports.error.fetching'));
      }
    } catch (err) {
      console.error('Error fetching live logs report:', err);
      setError(t('reports.error.fetching'));
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setGeneratingPDF(true);
    setError(null);
    try {
      const truckIds = selectedTrucks.length > 0 
        ? selectedTrucks.map(t => t.id).join(',') 
        : null;

      const response = await makeApiCall('api/reports/live-logs/pdf', {
        method: 'POST',
        body: JSON.stringify({
          from_date: fromDate ? fromDate.toISOString() : null,
          to_date: toDate ? toDate.toISOString() : null,
          truck_ids: truckIds,
          geofence_name: geofenceName
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `live-logs-report-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError(t('reports.error.pdf'));
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('reports.error.pdf'));
    } finally {
      setGeneratingPDF(false);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      fetchReport();
    }
  }, []); 

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleFromDateChange = (e) => {
    const d = e.target.value ? new Date(e.target.value) : null;
    if (d) d.setHours(0,0,0,0);
    setFromDate(d);
  };

  const handleToDateChange = (e) => {
    const d = e.target.value ? new Date(e.target.value) : null;
    if (d) d.setHours(23,59,59,999);
    setToDate(d);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('reports.filters')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.fromDate')}
              type="date"
              value={formatDateForInput(fromDate)}
              onChange={handleFromDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.toDate')}
              type="date"
              value={formatDateForInput(toDate)}
              onChange={handleToDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Autocomplete
              multiple
              options={trucks}
              getOptionLabel={(option) => option.name}
              value={selectedTrucks}
              onChange={(event, newValue) => setSelectedTrucks(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t('reports.liveLogs.truck')} 
                  placeholder={t('reports.selectTruck')}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              label={t('reports.liveLogs.geofence')}
              value={geofenceName}
              onChange={(e) => setGeofenceName(e.target.value)}
              placeholder="Search geofence name"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={{ ml: 'auto' }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchReport}
                disabled={loading || !fromDate || !toDate}
                sx={{ bgcolor: '#1976d2' }}
              >
                {t('reports.load')}
              </Button>
              <Button
                variant="contained"
                startIcon={generatingPDF ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={downloadPDF}
                disabled={generatingPDF || data.length === 0}
                sx={{ bgcolor: '#d32f2f' }}
              >
                PDF
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary */}
      {data.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#e0f2f1' }}>
          <CardContent>
            <Stack direction="row" spacing={3} alignItems="center">
              <LocationOnIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('reports.liveLogs.title')} Summary
              </Typography>
              <Chip label={`Total Records: ${data.length}`} color="primary" />
              <Chip 
                label={`Unique Geofences: ${new Set(data.map(d => d.geofence_name)).size}`} 
                color="secondary" 
                variant="outlined"
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Report Data */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('reports.noData')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell><strong>Date/Time</strong></TableCell>
                <TableCell><strong>{t('reports.liveLogs.truck')}</strong></TableCell>
                <TableCell><strong>{t('reports.liveLogs.geofence')}</strong></TableCell>
                <TableCell><strong>{t('reports.liveLogs.ignition')}</strong></TableCell>
                <TableCell><strong>{t('reports.liveLogs.speed')} (km/h)</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{formatDateTime(row.event_time_est)}</TableCell>
                  <TableCell>{row.truck_name || row.device_name}</TableCell>
                  <TableCell>
                    <Chip 
                      icon={<LocationOnIcon />} 
                      label={row.geofence_name} 
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                    />
                  </TableCell>
                  <TableCell>{row.ignition_status || '-'}</TableCell>
                  <TableCell>{row.speed_kph || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default LiveLogsReport;

